import React, { useState, useEffect, useCallback, useMemo } from 'react'; // Removed useRef as it's not directly used by top-level now
import { Table, Card, Row, Col, Form, Button, Modal, Spinner, Alert } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faDumbbell, faPersonRunning, faHeartPulse, faBrain, faChild, faHome, faBuilding, faTree, faCog,
    faHistory, faPlusCircle, faArrowLeft // Icons for ViewModeLayout
} from '@fortawesome/free-solid-svg-icons';
import { callPollinationsTextApi, DEFAULT_POLLINATIONS_TEXT_MODEL } from '../services/aiService';

// Import Reusable Components
import ViewModeLayout from './ui/ViewModeLayout'; // Adjust path
import HistoryView from './ui/HistoryView';       // Adjust path
import ItemDetailsModal from './ui/ItemDetailsModal'; // Adjust path

// Import DB Service functions
import { getWorkoutHistory, hideUserActivity } from '../services/dbService'; // visibleUserActivity might not be needed if auto-saving visible

// --- Constants ---
const IMAGE_API_BASE_POLLINATIONS = 'https://image.pollinations.ai/prompt/';
const EXERCISE_SEARCH_API_URL = 'http://localhost:8000/exercises/search';
const BACKEND_BASE_URL = 'http://localhost:8000';
const DB_IMAGE_BASE_URL = 'https://raw.githubusercontent.com/yuhonas/free-exercise-db/main/exercises/';
const POLLINATIONS_IMAGE_MODEL = 'flux';//'gptimage';//'flux'; // Default model for image generation

const WeeklyWorkoutGenerator = ({ userId, userHealthGoals, userPersonalInfo, onWorkoutGenerated }) => {

    // Log para ver la prop cuando el componente se monta o re-renderiza
    console.log("WeeklyWorkoutGenerator - userPersonalInfo prop received:", userPersonalInfo);

    const [viewMode, setViewMode] = useState('history'); // O 'generator' si prefieres que sea el inicial

    // --- Generator State (relacionado con la IA y el plan actual) ---
    const [llmWorkoutPlan, setLlmWorkoutPlan] = useState(null);
    const [matchedExercisesData, setMatchedExercisesData] = useState({});
    const [loadingGeneration, setLoadingGeneration] = useState(false);
    const [generationError, setGenerationError] = useState('');

    // --- Funciones Auxiliares para calcular valores iniciales del formulario ---
    // Estas funciones usan `userPersonalInfo` y `userHealthGoals` (que son props)
    // para determinar los valores iniciales.
    const calculateInitialAge = () => {
        // console.log("Calculating age. userPersonalInfo?.age:", userPersonalInfo?.age);
        return userPersonalInfo?.age ? parseInt(userPersonalInfo.age, 10) : 30;
    };

    const calculateInitialGender = () => {
        // console.log("Calculating gender. userPersonalInfo?.sex:", userPersonalInfo?.sex);
        if (userPersonalInfo?.sex) {
            const sexLower = userPersonalInfo.sex.toLowerCase();
            if (sexLower === 'female') return 'Female';
            if (sexLower === 'other') return 'Other';
            return 'Male'; // 'male' u otros casos
        }
        return 'Male'; // Default
    };

    const calculateInitialHeight = () => {
        // console.log("Calculating height. userPersonalInfo?.height:", userPersonalInfo?.height);
        return userPersonalInfo?.height ? parseInt(userPersonalInfo.height, 10) : 175;
    };

    const calculateInitialWeight = () => {
        // console.log("Calculating weight. userPersonalInfo?.weight:", userPersonalInfo?.weight);
        return userPersonalInfo?.weight ? parseFloat(userPersonalInfo.weight) : 75;
    };

    const calculateInitialFitnessLevel = () => {
        // console.log("Calculating fitness level. userPersonalInfo?.activity_level:", userPersonalInfo?.activity_level);
        if (userPersonalInfo?.activity_level) {
            const level = userPersonalInfo.activity_level.toLowerCase();
            if (level === 'sedentary' || level === 'light') return 'Beginner';
            if (level === 'moderate') return 'Intermediate';
            if (level === 'active' || level === 'very_active') return 'Advanced';
        }
        return 'Intermediate'; // Default
    };

    const calculateInitialPrimaryGoal = () => {
        // console.log("Calculating primary goal. userHealthGoals:", userHealthGoals, "userPersonalInfo?.primary_goal:", userPersonalInfo?.primary_goal);
        let goalToSet = 'Build Muscle'; // Default general
        let goalSet = false;

        if (Array.isArray(userHealthGoals) && userHealthGoals.length > 0) {
            const primaryHealthGoalObj = userHealthGoals.find(g => g.is_primary) || userHealthGoals[0];
            if (primaryHealthGoalObj?.target) {
                const healthGoalTarget = primaryHealthGoalObj.target.toLowerCase();
                if (healthGoalTarget.includes('fat loss') || healthGoalTarget.includes('lose weight')) { goalToSet = 'Fat Loss'; goalSet = true; }
                else if (healthGoalTarget.includes('muscle') || healthGoalTarget.includes('build muscle')) { goalToSet = 'Build Muscle'; goalSet = true; }
                else if (healthGoalTarget.includes('endurance') || healthGoalTarget.includes('improve endurance')) { goalToSet = 'Improve Endurance'; goalSet = true; }
                else if (healthGoalTarget.includes('flexibility') || healthGoalTarget.includes('increase flexibility')) { goalToSet = 'Increase Flexibility'; goalSet = true; }
            }
        }

        if (!goalSet && userPersonalInfo?.primary_goal) {
            const personalInfoGoal = userPersonalInfo.primary_goal.toLowerCase();
            if (personalInfoGoal.includes('lose_weight') || personalInfoGoal.includes('fat')) { goalToSet = 'Fat Loss'; }
            else if (personalInfoGoal.includes('build_muscle') || personalInfoGoal.includes('muscle')) { goalToSet = 'Build Muscle'; }
            else if (personalInfoGoal.includes('improve_endurance') || personalInfoGoal.includes('endurance')) { goalToSet = 'Improve Endurance'; }
            else if (personalInfoGoal.includes('increase_flexibility') || personalInfoGoal.includes('flexibility')) { goalToSet = 'Increase Flexibility'; }
            else if (personalInfoGoal.includes('stress_relief') || personalInfoGoal.includes('stress')) { goalToSet = 'Stress Relief'; }
            else if (personalInfoGoal.includes('general_fitness') || personalInfoGoal.includes('general')) { goalToSet = 'General Fitness'; }
        }
        return goalToSet;
    };

    // --- Form State (los valores que el usuario puede modificar en el formulario) ---
    // Se inicializan usando las funciones `calculateInitial...`.
    // Estos `useState` se ejecutan solo una vez en el montaje inicial del componente.
    // Si `userPersonalInfo` es undefined en ese momento, se usarán los defaults.
    const [age, setAge] = useState(calculateInitialAge());
    const [gender, setGender] = useState(calculateInitialGender());
    const [height, setHeight] = useState(calculateInitialHeight());
    const [weight, setWeight] = useState(calculateInitialWeight());
    const [fitnessLevel, setFitnessLevel] = useState(calculateInitialFitnessLevel());
    const [primaryGoal, setPrimaryGoal] = useState(calculateInitialPrimaryGoal());

    // Otros campos del formulario que no dependen directamente del perfil del usuario
    // o que el usuario define específicamente para este plan.
    const [daysAvailable, setDaysAvailable] = useState(3);
    const [durationPerSession, setDurationPerSession] = useState(60);
    const [trainingLocation, setTrainingLocation] = useState('Gym');
    const [availableEquipment, setAvailableEquipment] = useState('');
    const [activityTypePreference, setActivityTypePreference] = useState('Gym Workout (Strength)');
    const [specificActivityOrSport, setSpecificActivityOrSport] = useState('');

    // --- History State ---
    const [workoutHistory, setWorkoutHistory] = useState([]);
    const [isLoadingHistory, setIsLoadingHistory] = useState(false);
    const [historyError, setHistoryError] = useState('');
    const [selectedHistoryWorkout, setSelectedHistoryWorkout] = useState(null);
    const [showHistoryModal, setShowHistoryModal] = useState(false);

    // --- Modal for individual activity details (within a plan) ---
    const [showActivityDetailModal, setShowActivityDetailModal] = useState(false);
    const [selectedLlmActivityForDetail, setSelectedLlmActivityForDetail] = useState(null);
    const [isModalImageHovering, setIsModalImageHovering] = useState(false);

    useEffect(() => {
        console.log("useEffect [userPersonalInfo, userHealthGoals] triggered. Current userPersonalInfo:", userPersonalInfo);

        // Solo actualiza si userPersonalInfo tiene un valor, para evitar resetear a defaults
        // si userPersonalInfo pasa a ser undefined temporalmente por alguna razón.
        if (userPersonalInfo) {
            setAge(calculateInitialAge());
            setGender(calculateInitialGender());
            setHeight(calculateInitialHeight());
            setWeight(calculateInitialWeight());
            setFitnessLevel(calculateInitialFitnessLevel());
        }
        // PrimaryGoal puede depender también de userHealthGoals, así que se actualiza siempre
        setPrimaryGoal(calculateInitialPrimaryGoal());

        console.log("Form states after useEffect update attempt:", {
            age: calculateInitialAge(), // Muestra lo que se intentó setear
            gender: calculateInitialGender(),
            height: calculateInitialHeight(),
            weight: calculateInitialWeight(),
            fitnessLevel: calculateInitialFitnessLevel(),
            primaryGoal: calculateInitialPrimaryGoal()
        });

    }, [userPersonalInfo, userHealthGoals, calculateInitialAge, calculateInitialGender, calculateInitialHeight, calculateInitialWeight, calculateInitialFitnessLevel, calculateInitialPrimaryGoal]);



    useEffect(() => {
        if (userHealthGoals && userHealthGoals.length > 0 && userHealthGoals[0].target) {
            const goalTarget = userHealthGoals[0].target.toLowerCase();
            if (goalTarget.includes('fat loss')) setPrimaryGoal('Fat Loss');
            else if (goalTarget.includes('muscle')) setPrimaryGoal('Build Muscle');
            else if (goalTarget.includes('endurance')) setPrimaryGoal('Improve Endurance');
            else if (goalTarget.includes('flexibility')) setPrimaryGoal('Increase Flexibility');
        }
    }, [userHealthGoals]);

    useEffect(() => { if (daysAvailable < 1) setDaysAvailable(1); else if (daysAvailable > 7) setDaysAvailable(7); }, [daysAvailable]);

    const fetchAndSetHistory = useCallback(async () => {
        if (userId) {
            setIsLoadingHistory(true); setHistoryError('');
            try {
                const history = await getWorkoutHistory(userId);
                setWorkoutHistory(history || []);
            } catch (err) {
                setHistoryError('Could not load workout history.');
            } finally { setIsLoadingHistory(false); }
        }
    }, [userId]);

    useEffect(() => { if (viewMode === 'history') { fetchAndSetHistory(); } }, [viewMode, fetchAndSetHistory]);

    const generateImagePrompt = (activityName, categoryOrFocus, trainingLoc) => {
        let prompt = `Professional fitness photography of a person performing ${activityName}, demonstrating correct form and technique. `;

        prompt += `Theme: ${categoryOrFocus || 'exercise demonstration'}. `;

        // Agregar estilo fotográfico
        prompt += "Photography style: high-resolution, sharp focus, vibrant colors, realistic human anatomy. ";

        // Ubicación con más detalle
        switch (trainingLoc) {
            case 'Gym':
                prompt += "Setting: modern, well-lit gym with equipment visible, clean and spacious. ";
                break;
            case 'Outdoors':
                prompt += "Setting: lively outdoor park, natural sunlight, green surroundings. ";
                break;
            case 'Home':
                prompt += "Setting: clean and organized home workout area, cozy atmosphere. ";
                break;
            default:
                prompt += "Setting: neutral, minimalistic background. ";
                break;
        }

        // Atmósfera y acción según tipo de ejercicio
        if (categoryOrFocus?.toLowerCase().includes('yoga') || activityName.toLowerCase().includes('yoga')) {
            prompt += "Mood: serene, calm, peaceful atmosphere with soft lighting. ";
            prompt += "Pose: clear and balanced yoga posture. ";
        } else if (categoryOrFocus?.toLowerCase().includes('cardio') ||
            activityName.toLowerCase().includes('running') ||
            activityName.toLowerCase().includes('cycling')) {
            prompt += "Mood: energetic, dynamic motion blur effects, outdoor vibe if applicable. ";
        } else if (categoryOrFocus?.toLowerCase().includes('strength') ||
            categoryOrFocus?.toLowerCase().includes('weightlifting')) {
            prompt += "Mood: focused, intense expression, muscles visible, gym environment emphasized. ";
        }

        // Agregar tipo de plano si quieres más variedad
        prompt += "Shot type: full body, action shot, realistic proportions.";

        return prompt;
    };


    const fetchAndEnrichActivityData = useCallback(async (activityFromLlmPlan, sessionFocus) => {
        // ... (fetchAndEnrichActivityData logic - NO CHANGE from your provided code)
        const activityName = activityFromLlmPlan["Activity Name"];
        if (!activityName || typeof activityName !== 'string') return;
        setMatchedExercisesData(prev => ({ ...prev, [activityName]: { source: 'pending_db', llmName: activityName, displayName: activityName, aiGeneratedFields: { ...activityFromLlmPlan }, dbData: null, description_ai: null, imageUrl_ai: null, } }));
        let enrichedData = { source: 'none', llmName: activityName, displayName: activityName, aiGeneratedFields: { ...activityFromLlmPlan }, dbData: null, description_ai: null, imageUrl_ai: null, };
        const gymLikeTypes = ['gym workout', 'strength training', 'bodybuilding', 'weightlifting', 'resistance training'];
        const isGymActivity = gymLikeTypes.some(type => sessionFocus?.toLowerCase().includes(type)) || activityTypePreference.toLowerCase().includes('gym') || activityTypePreference.toLowerCase().includes('strength');
        if (isGymActivity) { try { const searchUrl = `${EXERCISE_SEARCH_API_URL}?q=${encodeURIComponent(activityName)}&limit=1`; const response = await fetch(searchUrl); if (response.ok) { const searchResults = await response.json(); if (searchResults && searchResults.length > 0) { const dbMatch = searchResults[0]; enrichedData.dbData = dbMatch; enrichedData.displayName = dbMatch.name || activityName; enrichedData.source = 'db'; if (dbMatch.images?.length > 0 && dbMatch.instructions?.length > 0) { setMatchedExercisesData(prev => ({ ...prev, [activityName]: enrichedData })); return; } } } else { console.warn(`DB search for ${activityName} failed: ${response.status}`); } } catch (dbError) { console.error(`Error searching DB for ${activityName}:`, dbError); } }
        enrichedData.source = 'pending_ai'; setMatchedExercisesData(prev => ({ ...prev, [activityName]: { ...enrichedData } }));


        try {
            const descSystemPrompt = `You are a fitness expert. Provide concise, clear instructions on how to perform the following activity/exercise. If it's a common exercise, include key form tips. If it's a general activity (e.g., 'Jogging'), describe it briefly and mention benefits. Focus on safety and effectiveness. Max 3-4 sentences.`;
            const descUserPrompt = `Activity: "${activityName}". Session Focus: "${sessionFocus || 'General Fitness'}".`;
            const aiDescription = await callPollinationsTextApi(descSystemPrompt, descUserPrompt, DEFAULT_POLLINATIONS_TEXT_MODEL, false);
            enrichedData.description_ai = aiDescription || "AI could not generate a description.";

            // Generar prompt para la imagen
            const activityContextForImage = enrichedData.aiGeneratedFields?.Category || sessionFocus || 'fitness activity';

            // trainingLocation es del estado del componente, debe estar en las dependencias de useCallback
            console.log(`Generating image prompt for ${activityName} with context "${activityContextForImage}" and training location "${trainingLocation}"`);
            const imagePrompt = generateImagePrompt(activityName, activityContextForImage, trainingLocation);
            console.log(`Generated image prompt for ${activityName}:`, imagePrompt);
            enrichedData.imageUrl_ai = `${IMAGE_API_BASE_POLLINATIONS}${encodeURIComponent(imagePrompt)}?width=400&height=300&model=${POLLINATIONS_IMAGE_MODEL}&nologo=true&seed=${Math.floor(Math.random() * 10000)}`; enrichedData.source = 'ai_fallback';


            //const imagePrompt = generateImagePrompt(activityName, focus, trainingLocation); // Asumes que trainingLocation está disponible en este scope, si no, pásala.

            //const imagePrompt = `${activityName}, ${enrichedData.aiGeneratedFields?.Category || sessionFocus || 'fitness activity'}, clear demonstration, well-lit, high detail, photorealistic`; enrichedData.imageUrl_ai = `${IMAGE_API_BASE_POLLINATIONS}${encodeURIComponent(imagePrompt)}?width=400&height=300&model=flux&nologo=true&seed=${Math.floor(Math.random() * 10000)}`; enrichedData.source = 'ai_fallback';
        } catch (aiError) {
            console.error(`Error fetching AI details for ${activityName}:`, aiError); enrichedData.source = 'none'; if (!enrichedData.description_ai) enrichedData.description_ai = "Could not load details.";
        }
        setMatchedExercisesData(prev => ({ ...prev, [activityName]: enrichedData }));
    }, [activityTypePreference]);


    const generateActivityPlan = useCallback(async () => {
        setLoadingGeneration(true);
        setGenerationError('');
        setLlmWorkoutPlan(null);
        setMatchedExercisesData({});

        let finalActivityType = activityTypePreference;
        if ((activityTypePreference === 'Other' || activityTypePreference === 'Sport-Specific') && specificActivityOrSport.trim()) {
            finalActivityType = `${activityTypePreference}: ${specificActivityOrSport.trim()}`;
        } else if ((activityTypePreference === 'Other' || activityTypePreference === 'Sport-Specific') && !specificActivityOrSport.trim()) {
            setGenerationError("Please specify the 'Other' activity or sport.");
            setLoadingGeneration(false);
            return;
        }

        const goalsString = userHealthGoals && userHealthGoals.length > 0
            ? ` Goals: ${userHealthGoals.map(g => g.target).join(', ')}.`
            : '';

        // --- SECCIÓN CLAVE PARA AÑADIR INFORMACIÓN CALÓRICA AL PROMPT ---
        let calorieInfo = "";
        if (userPersonalInfo) { // userPersonalInfo es la prop del componente
            if (userPersonalInfo.target_calories && userPersonalInfo.target_calories > 0) {
                calorieInfo += ` The user's target daily calorie intake is approximately ${userPersonalInfo.target_calories} kcal.`;
            } else if (userPersonalInfo.tdee && userPersonalInfo.tdee > 0) {
                // Solo añadir TDEE si target_calories no está o es 0, para no ser redundante si target_calories ya considera TDEE
                calorieInfo += ` The user's estimated Total Daily Energy Expenditure (TDEE) is ${userPersonalInfo.tdee} kcal.`;
            } else if (userPersonalInfo.bmr && userPersonalInfo.bmr > 0) {
                // Solo añadir BMR si los otros dos no están
                calorieInfo += ` The user's estimated Basal Metabolic Rate (BMR) is ${userPersonalInfo.bmr} kcal.`;
            }
        }

        // Añadir contexto al calorieInfo basado en el primaryGoal
        if (primaryGoal === 'Fat Loss' && calorieInfo.length > 0) {
            calorieInfo += " The activity plan should aim to contribute to a caloric deficit, focusing on activities with higher energy expenditure or tips related to energy balance.";
        } else if (primaryGoal === 'Build Muscle' && calorieInfo.length > 0) {
            calorieInfo += " The activity plan should support muscle growth. Consider activities that stimulate hypertrophy and tips related to protein intake or recovery, aligning with potential caloric surplus or maintenance needs.";
        }
        // --- FIN DE LA SECCIÓN CLAVE ---

        const userDetails = `User Profile: Age ${age}, Gender ${gender}, Height ${height}cm, Weight ${weight}kg. Fitness Level: ${fitnessLevel}. Primary Goal: ${primaryGoal}.${goalsString} Availability: ${daysAvailable} days/week, ${durationPerSession} min/session. Training Location: ${trainingLocation}. Available Equipment: ${availableEquipment || 'None'}. Preferred Activity Type: ${finalActivityType}.${calorieInfo}`; // <<-- calorieInfo AÑADIDO AQUÍ


        // Mensaje para depuración

        const systemInstruction = `You are an expert wellness and activity planning AI. Based on the user's profile and preferences (including any provided caloric information like BMR, TDEE, or target calories), generate a diverse and engaging weekly activity plan for ${daysAvailable} sessions. For each session: Define "Session Focus". List "Activities", each with "Activity Name". Include relevant details (Sets/Reps for strength; Duration/Distance/Intensity for cardio; Pose/Hold Time for yoga). Optionally "Estimated Time". Add one "Daily Tip" that can be general or specifically leverage user's profile data (e.g., if aiming for fat loss and TDEE is known, a tip could relate to overall energy balance). Output MUST be valid JSON. All keys/strings in English. Prioritize common activities. Example: {"Day 1": {"Session Focus": "Full Body Strength (Gym)", "Activities": [{"Activity Name": "Squats", "Sets": 4, "Reps": "8-10"}], "Daily Tip": "Warm up before starting your workout."}} Return ONLY JSON.`;
        const userPrompt = `Generate plan based on: ${userDetails}`;
        console.log("Generating activity plan with system instruction:", systemInstruction);
        console.log("User prompt for activity plan generation:", userPrompt);

        try {
            const generatedPlanFromAI = await callPollinationsTextApi(systemInstruction, userPrompt);
            if (!generatedPlanFromAI || typeof generatedPlanFromAI !== 'object' || Object.keys(generatedPlanFromAI).length === 0) {
                throw new Error('Invalid/empty plan from API. The AI might have returned a non-JSON response or an empty object.');
            }
            setLlmWorkoutPlan(generatedPlanFromAI);

            if (onWorkoutGenerated && typeof onWorkoutGenerated === 'function') {
                await onWorkoutGenerated(generatedPlanFromAI, true);
                console.log("Workout plan generated and logged as visible.");
                fetchAndSetHistory();
            }

            const enrichmentPromises = [];
            for (const dayKey in generatedPlanFromAI) {
                const dayData = generatedPlanFromAI[dayKey];
                if (dayData && Array.isArray(dayData["Activities"])) {
                    dayData["Activities"].forEach(activity => {
                        if (activity && activity["Activity Name"]) {
                            // Aquí fetchAndEnrichActivityData NO necesita calorieInfo explícitamente,
                            // ya que el plan general ya fue influenciado por ello.
                            // Si quisieras que la descripción de CADA ejercicio también lo considerara,
                            // necesitarías pasar userPersonalInfo a fetchAndEnrichActivityData
                            // y modificar su prompt interno (como hicimos por error antes).
                            enrichmentPromises.push(fetchAndEnrichActivityData(activity, dayData["Session Focus"]));
                        }
                    });
                }
            }
            await Promise.all(enrichmentPromises);
        } catch (mainError) {
            console.error("Error during workout plan generation or enrichment:", mainError);
            // Podrías querer mostrar el error crudo de la API si está disponible
            let detailedErrorMessage = `Error generating plan: ${mainError.message}.`;
            if (mainError.response && mainError.response.data) { // Ejemplo si el error viene de Axios
                detailedErrorMessage += ` API Response: ${JSON.stringify(mainError.response.data)}`;
            }
            setGenerationError(detailedErrorMessage);
            setLlmWorkoutPlan(null);
        } finally {
            setLoadingGeneration(false);
        }
    }, [
        age, gender, height, weight, fitnessLevel, primaryGoal, daysAvailable,
        durationPerSession, trainingLocation, availableEquipment, activityTypePreference,
        specificActivityOrSport,
        fetchAndEnrichActivityData, // Dependencia para el enriquecimiento posterior
        onWorkoutGenerated,
        userHealthGoals,
        userPersonalInfo, // <<-- AÑADIDO userPersonalInfo A LAS DEPENDENCIAS
        fetchAndSetHistory
    ]);

    /*
    const generateActivityPlan = useCallback(async () => {
        setLoadingGeneration(true); setGenerationError(''); setLlmWorkoutPlan(null); setMatchedExercisesData({});
        let finalActivityType = activityTypePreference;
        if ((activityTypePreference === 'Other' || activityTypePreference === 'Sport-Specific') && specificActivityOrSport.trim()) {
            finalActivityType = `${activityTypePreference}: ${specificActivityOrSport.trim()}`;
        } else if ((activityTypePreference === 'Other' || activityTypePreference === 'Sport-Specific') && !specificActivityOrSport.trim()) {
            setGenerationError("Please specify the 'Other' activity or sport.");
            setLoadingGeneration(false); return;
        }
        const goalsString = userHealthGoals && userHealthGoals.length > 0 ? ` Goals: ${userHealthGoals.map(g => g.target).join(', ')}.` : '';
        const userDetails = `User Profile: Age ${age}, Gender ${gender}, Height ${height}cm, Weight ${weight}kg. Fitness Level: ${fitnessLevel}. Primary Goal: ${primaryGoal}.${goalsString} Availability: ${daysAvailable} days/week, ${durationPerSession} min/session. Training Location: ${trainingLocation}. Available Equipment: ${availableEquipment || 'None'}. Preferred Activity Type: ${finalActivityType}.`;
        const systemInstruction = `You are an expert wellness and activity planning AI. Based on the user's profile and preferences, generate a diverse and engaging weekly activity plan for ${daysAvailable} sessions. For each session: Define "Session Focus". List "Activities", each with "Activity Name". Include relevant details (Sets/Reps for strength; Duration/Distance/Intensity for cardio; Pose/Hold Time for yoga). Optionally "Estimated Time". Add one "Daily Tip". Output MUST be valid JSON. All keys/strings in English. Prioritize common activities. Example: {"Day 1": {"Session Focus": "Full Body Strength (Gym)", "Activities": [{"Activity Name": "Squats", "Sets": 4, "Reps": "8-10"}], "Daily Tip": "Warm up."}} Return ONLY JSON.`;

        try {
            const generatedPlanFromAI = await callPollinationsTextApi(systemInstruction, `Generate plan based on: ${userDetails}`);
            if (!generatedPlanFromAI || typeof generatedPlanFromAI !== 'object' || Object.keys(generatedPlanFromAI).length === 0) {
                throw new Error('Invalid/empty plan from API.');
            }
            setLlmWorkoutPlan(generatedPlanFromAI); // For immediate display in generator view

            if (onWorkoutGenerated && typeof onWorkoutGenerated === 'function') {
                // onWorkoutGenerated (from MainPage) should save this with visible: true
                // and it should return the saved activity if we needed its ID, but for auto-save we might not.
                await onWorkoutGenerated(generatedPlanFromAI, true); // Save as visible
                console.log("Workout plan generated and logged as visible.");
                fetchAndSetHistory(); // Refresh history list
            }

            const enrichmentPromises = [];
            for (const dayKey in generatedPlanFromAI) {
                const dayData = generatedPlanFromAI[dayKey];
                if (dayData && Array.isArray(dayData["Activities"])) {
                    dayData["Activities"].forEach(activity => {
                        if (activity && activity["Activity Name"]) {
                            enrichmentPromises.push(fetchAndEnrichActivityData(activity, dayData["Session Focus"]));
                        }
                    });
                }
            }
            await Promise.all(enrichmentPromises);
        } catch (mainError) {
            setGenerationError(`Error generating plan: ${mainError.message}.`);
            setLlmWorkoutPlan(null);
        } finally {
            setLoadingGeneration(false);
        }
    }, [age, gender, height, weight, fitnessLevel, primaryGoal, daysAvailable, durationPerSession, trainingLocation, availableEquipment, activityTypePreference, specificActivityOrSport, fetchAndEnrichActivityData, onWorkoutGenerated, userHealthGoals, fetchAndSetHistory]);
*/
    // --- Navigation & Modal Handlers ---
    const handleShowGeneratorView = () => {
        setLlmWorkoutPlan(null); setMatchedExercisesData({}); setGenerationError(''); // Clear previous generation
        // Reset form fields for a new generation
        setAge(30); setGender('Male'); setHeight(175); setWeight(75);
        setFitnessLevel('Intermediate'); setPrimaryGoal('Build Muscle');
        setDaysAvailable(3); setDurationPerSession(60); setTrainingLocation('Gym');
        setAvailableEquipment(''); setActivityTypePreference('Gym Workout (Strength)');
        setSpecificActivityOrSport('');
        setViewMode('generator');
    };
    const handleShowHistoryView = () => { setViewMode('history'); };

    const handleShowHistoryDetails = (workoutItem) => { setSelectedHistoryWorkout(workoutItem); setShowHistoryModal(true); };
    const handleCloseHistoryModal = () => { setSelectedHistoryWorkout(null); setShowHistoryModal(false); };

    const handleHideWorkoutFromHistory = async (workoutIdToHide, e) => {
        e.stopPropagation();
        if (!workoutIdToHide || !userId) { setHistoryError("Cannot hide workout: missing ID or user info."); return; }
        if (window.confirm("Are you sure you want to hide this workout plan from your history?")) {
            setIsLoadingHistory(true);
            try {
                const success = await hideUserActivity(workoutIdToHide, userId);
                if (success) { setWorkoutHistory(prev => prev.filter(item => item.id !== workoutIdToHide)); setHistoryError(''); }
                else { setHistoryError("Failed to hide workout plan."); }
            } catch (err) { setHistoryError("An error occurred while hiding the workout plan."); }
            finally { setIsLoadingHistory(false); }
        }
    };

    // For individual exercise details modal
    const openActivityDetailModal = useCallback((activityNameFromPlan) => {
        const activityDetails = matchedExercisesData[activityNameFromPlan] || selectedHistoryWorkout?.enrichedActivities?.[activityNameFromPlan];
        if (activityDetails) {
            setSelectedLlmActivityForDetail(activityDetails);
            setIsModalImageHovering(false);
            setShowActivityDetailModal(true);
        } else { console.warn("Details not found for:", activityNameFromPlan); }
    }, [matchedExercisesData, selectedHistoryWorkout]);
    const closeActivityDetailModal = useCallback(() => { setShowActivityDetailModal(false); setSelectedLlmActivityForDetail(null); }, []);


    // --- Render Functions ---
    const renderGeneratorForm = () => (
        // ... (Your existing renderForm() JSX - NO CHANGES NEEDED to its internal structure)
        // The button will now be type="button" and call a wrapper if needed, or just stay type="submit"
        <Form className="mb-4 p-3 border rounded bg-light shadow-sm" onSubmit={(e) => { e.preventDefault(); generateActivityPlan(); }}>
            <h4 className="mb-3 text-primary">Your Profile & Preferences</h4>
            <Row><Col md={3}><Form.Group className="mb-3"><Form.Label>Age</Form.Label><Form.Control type="number" value={age} onChange={e => setAge(parseInt(e.target.value) || 0)} /></Form.Group></Col><Col md={3}><Form.Group className="mb-3"><Form.Label>Gender</Form.Label><Form.Select value={gender} onChange={e => setGender(e.target.value)}><option>Male</option><option>Female</option><option>Other</option></Form.Select></Form.Group></Col><Col md={3}><Form.Group className="mb-3"><Form.Label>Height (cm)</Form.Label><Form.Control type="number" value={height} onChange={e => setHeight(parseInt(e.target.value) || 0)} /></Form.Group></Col><Col md={3}><Form.Group className="mb-3"><Form.Label>Weight (kg)</Form.Label><Form.Control type="number" value={weight} onChange={e => setWeight(parseInt(e.target.value) || 0)} /></Form.Group></Col></Row>
            <Row><Col md={6}><Form.Group className="mb-3"><Form.Label>Fitness Level</Form.Label><Form.Select value={fitnessLevel} onChange={e => setFitnessLevel(e.target.value)}><option>Beginner</option><option>Intermediate</option><option>Advanced</option></Form.Select></Form.Group></Col><Col md={6}><Form.Group className="mb-3"><Form.Label>Primary Goal</Form.Label><Form.Select value={primaryGoal} onChange={e => setPrimaryGoal(e.target.value)}><option>Fat Loss</option><option>Build Muscle</option><option>Improve Endurance</option><option>Increase Flexibility</option><option>Stress Relief</option><option>General Fitness</option><option>Learn a New Skill</option></Form.Select></Form.Group></Col></Row>
            <h5 className="mt-3 mb-2 text-secondary">Activity Plan Details</h5>
            <Row><Col md={activityTypePreference === 'Sport-Specific' || activityTypePreference === 'Other' ? 4 : 12}><Form.Group className="mb-3"><Form.Label>Preferred Activity Type</Form.Label><Form.Select value={activityTypePreference} onChange={e => setActivityTypePreference(e.target.value)}><option>Gym Workout (Strength)</option><option>Gym Workout (Cardio Focus)</option><option>Home Bodyweight Workout</option><option>Running Plan</option><option>Cycling Plan</option><option>Yoga Routine</option><option>Pilates Session</option><option>Flexibility/Mobility</option><option>Sport-Specific</option><option>Mindfulness/Meditation</option><option>Other</option></Form.Select></Form.Group></Col>{(activityTypePreference === 'Sport-Specific' || activityTypePreference === 'Other') && (<Col md={8}><Form.Group className="mb-3"><Form.Label>Specify Sport/Activity</Form.Label><Form.Control type="text" value={specificActivityOrSport} onChange={e => setSpecificActivityOrSport(e.target.value)} placeholder="e.g., Soccer, Dance, Handstand Practice" /></Form.Group></Col>)}</Row>
            <Row><Col md={4}><Form.Group className="mb-3"><Form.Label>Days/Week</Form.Label><Form.Control type="number" value={daysAvailable} min={1} max={7} onChange={e => setDaysAvailable(parseInt(e.target.value) || 1)} /></Form.Group></Col><Col md={4}><Form.Group className="mb-3"><Form.Label>Duration/Session (min)</Form.Label><Form.Control type="number" value={durationPerSession} min={10} step={5} onChange={e => setDurationPerSession(parseInt(e.target.value) || 30)} /></Form.Group></Col><Col md={4}><Form.Group className="mb-3"><Form.Label>Training Location</Form.Label><Form.Select value={trainingLocation} onChange={e => setTrainingLocation(e.target.value)}><option>Home</option><option>Gym</option><option>Outdoors</option><option>Any</option></Form.Select></Form.Group></Col></Row>
            <Row><Col md={12}><Form.Group className="mb-3"><Form.Label>Available Equipment</Form.Label><Form.Control type="text" value={availableEquipment} placeholder="e.g., None, Dumbbells, Yoga Mat, Resistance Bands" onChange={e => setAvailableEquipment(e.target.value)} /></Form.Group></Col></Row>
            <div className="text-center">
                <Button type="submit" variant="success" size="lg" className="shadow-sm" disabled={loadingGeneration}>
                    {loadingGeneration ? <><Spinner as="span" animation="border" size="sm" /> Generating Your Plan...</> : 'Generate Activity Plan'}
                </Button>
            </div>
            {loadingGeneration && <p className="mt-2 text-info fst-italic small">Creating your plan and enriching activities... This might take a moment.</p>}
        </Form>
    );

    // This function renders the details of a workout plan (either new or from history)
    const renderWorkoutPlanDetailsContent = (planData) => {
        // planData is either llmWorkoutPlan (for new) or selectedHistoryWorkout.workoutPlan (for history)
        if (!planData || Object.keys(planData).length === 0) return <Alert variant="light">No plan data to display.</Alert>;

        const planToRender = planData.workoutPlan || planData; // Handle if workoutPlan is nested

        return (
            <div className="mt-0"> {/* Removed top margin for cleaner modal integration */}
                {Object.entries(planToRender).map(([dayKey, dayData]) => (
                    <Card key={dayKey} className="mb-4 shadow-sm">
                        <Card.Header as="h5" className="bg-info text-white">{dayKey}: <span className="fw-normal fs-6">{dayData["Session Focus"] || "Activities"}</span></Card.Header>
                        <Card.Body>
                            {Array.isArray(dayData["Activities"]) && dayData["Activities"].length > 0 ? (
                                <Table striped bordered hover responsive size="sm" className="workout-activities-table">
                                    <thead><tr><th>Activity</th><th>Details</th><th>Est. Time</th><th>Image</th><th>Info</th></tr></thead>
                                    <tbody>
                                        {dayData["Activities"].map((activity, index) => {
                                            const activityName = activity["Activity Name"];
                                            // For history items, enriched data might be stored differently or fetched on demand
                                            // For newly generated, matchedExercisesData is used.
                                            const enrichedInfo = matchedExercisesData[activityName] || selectedHistoryWorkout?.enrichedActivities?.[activityName] || { llmName: activityName, displayName: activityName, aiGeneratedFields: activity };
                                            const details = `${activity["Sets"] ? `Sets: ${activity["Sets"]}` : ''} ${activity["Reps"] ? `Reps: ${activity["Reps"]}` : ''} ${activity["Duration"] ? `Dur: ${activity["Duration"]}` : ''} ${activity["Distance"] ? `Dist: ${activity["Distance"]}` : ''} ${activity["Intensity"] ? `Int: ${activity["Intensity"]}` : ''} ${activity["Hold Time"] ? `Hold: ${activity["Hold Time"]}` : ''}`.trim();
                                            return (
                                                <tr key={`${dayKey}-act-${index}`}>
                                                    <td className="fw-medium">{enrichedInfo.displayName}</td><td>{details || 'N/A'}</td>
                                                    <td>{activity["Estimated Time"] || 'N/A'}</td>
                                                    <td><ActivityImageDisplay activityName={activityName} enrichedData={enrichedInfo} /></td>
                                                    <td><Button variant="outline-info" size="sm" onClick={() => openActivityDetailModal(activityName)}>Details</Button></td>
                                                </tr>);
                                        })}
                                    </tbody>
                                </Table>
                            ) : <p className="text-muted">No activities listed for this day.</p>}
                            {dayData["Daily Tip"] && <Alert variant="light" className="mt-3 p-2 border fst-italic small"><strong>Tip:</strong> {dayData["Daily Tip"]}</Alert>}
                        </Card.Body>
                    </Card>
                ))}
            </div>
        );
    };
    const ActivityImageDisplay = ({ activityName, enrichedData }) => { /* ... (NO CHANGE from your provided code) ... */
        const [isHovering, setIsHovering] = useState(false); const containerStyle = { width: '80px', height: '80px', overflow: 'hidden', borderRadius: '4px', display: 'flex', justifyContent: 'center', alignItems: 'center', backgroundColor: '#f0f0f0', cursor: 'default' }; const imageStyle = { display: 'block', maxWidth: '100%', maxHeight: '100%', width: 'auto', height: 'auto', objectFit: 'contain' }; if (!enrichedData || enrichedData.source === 'pending_db' || enrichedData.source === 'pending_ai') { return <div style={containerStyle}><Spinner animation="border" size="sm" title="Loading details..." /></div>; } let displaySrc = enrichedData.imageUrl_ai; let canShowGif = false; let gifUrl = null; if (enrichedData.source === 'db' && enrichedData.dbData?.images?.length > 0) { displaySrc = `${DB_IMAGE_BASE_URL}${enrichedData.dbData.images[0]}`; if (enrichedData.dbData.id && enrichedData.dbData.images.length >= 2) { gifUrl = `${BACKEND_BASE_URL}/exercises/${encodeURIComponent(enrichedData.dbData.id)}/gif?duration_ms=400`; canShowGif = true; containerStyle.cursor = 'pointer'; } } const currentImage = isHovering && canShowGif ? gifUrl : displaySrc; if (!currentImage) return <div style={containerStyle}><span style={{ fontSize: '0.7rem', color: '#6c757d' }}>No img</span></div>; return (<div style={containerStyle} onMouseEnter={() => canShowGif && setIsHovering(true)} onMouseLeave={() => canShowGif && setIsHovering(false)} title={enrichedData.displayName}><img src={currentImage} alt={enrichedData.displayName} style={imageStyle} onError={(e) => { if (e.target.src === gifUrl && enrichedData.source === 'db' && enrichedData.dbData?.images?.[0]) e.target.src = `${DB_IMAGE_BASE_URL}${enrichedData.dbData.images[0]}`; else if (e.target.src === enrichedData.imageUrl_ai) e.target.style.display = 'none'; else { const p = e.target.parentNode; if (p) p.innerHTML = '<span style="font-size:0.7rem;color:#dc3545;">Err</span>'; } }} /></div>);
    };


    // This renders the summary of a workout plan for the HistoryView list
    const renderWorkoutHistoryItemSummary = (historyItem) => {
        // historyItem is { id, timestamp, workoutPlan: { ... }, name: "..." }
        const planName = historyItem.name || `Workout Plan - ${new Date(historyItem.timestamp).toLocaleDateString()}`;
        const firstDayKey = Object.keys(historyItem.workoutPlan || {})[0];
        const firstSessionFocus = firstDayKey ? (historyItem.workoutPlan[firstDayKey]["Session Focus"] || "General Workout") : "Workout Plan";

        return (
            <>
                <div className="fw-bold"><FontAwesomeIcon icon={faDumbbell} className="me-2 text-success" />{planName}</div>
                <span className="text-muted small">Focus: {firstSessionFocus.length > 70 ? firstSessionFocus.substring(0, 70) + "..." : firstSessionFocus}</span>
            </>
        );
    };

    // Modal for individual exercise details (this is specific to WorkoutGenerator)
    const renderActivityDetailModal = () => {
        if (!selectedLlmActivityForDetail) return null;

        const { displayName, description_ai, imageUrl_ai, dbData, aiGeneratedFields, source } = selectedLlmActivityForDetail;
        const modalImageContainerStyle = { width: '100%', maxHeight: '350px', overflow: 'hidden', borderRadius: '4px', display: 'flex', justifyContent: 'center', alignItems: 'center', backgroundColor: '#e9ecef', marginBottom: '1rem', cursor: 'default' };
        const modalImageStyle = { display: 'block', maxWidth: '100%', maxHeight: '100%', width: 'auto', height: 'auto', objectFit: 'contain' };
        let staticModalImageUrl = null, gifModalUrl = null, canShowGif = false;

        if (source === 'db' && dbData?.images?.length > 0) {
            staticModalImageUrl = `${DB_IMAGE_BASE_URL}${dbData.images[0]}`;
            if (dbData.id && dbData.images.length >= 2) {
                gifModalUrl = `${BACKEND_BASE_URL}/exercises/${encodeURIComponent(dbData.id)}/gif?duration_ms=400`;
                canShowGif = true;
                modalImageContainerStyle.cursor = 'pointer';
            }
        } else if (imageUrl_ai) {
            staticModalImageUrl = imageUrl_ai;
        }
        const currentImageSrc = isModalImageHovering && gifModalUrl ? gifModalUrl : staticModalImageUrl;

        // Helper function to render description_ai, whether string or specific object
        const renderDescriptionContent = (desc) => {
            if (typeof desc === 'string') {
                return <p className="small" style={{ whiteSpace: 'pre-wrap' }}>{desc}</p>;
            }
            if (typeof desc === 'object' && desc !== null) {
                // Specifically handle the {activity, instructions} structure
                if (desc.activity || desc.instructions) {
                    return (
                        <div className="small">
                            {desc.activity && (
                                <p className="mb-1"><strong>Activity:</strong> {String(desc.activity)}</p>
                            )}
                            {desc.instructions && (
                                <div>
                                    <strong>Instructions:</strong>
                                    <p style={{ whiteSpace: 'pre-wrap' }} className="mb-0">{String(desc.instructions)}</p>
                                </div>
                            )}
                        </div>
                    );
                }
                // Fallback for other object structures
                return <pre className="small" style={{ whiteSpace: 'pre-wrap', overflowX: 'auto' }}>{JSON.stringify(desc, null, 2)}</pre>;
            }
            return <p className="small fst-italic text-muted">No description available.</p>;
        };

        return (
            <Modal show={showActivityDetailModal} onHide={closeActivityDetailModal} centered size="lg">
                <Modal.Header closeButton>
                    <Modal.Title>{displayName}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Row>
                        <Col md={staticModalImageUrl ? 6 : 12}>
                            <h5 className="text-primary">Planned Details (AI)</h5>
                            {Object.entries(aiGeneratedFields || {}).map(([key, value]) => {
                                const displayKey = key.replace(/([A-Z])/g, ' $1').replace(/^./, c => c.toUpperCase());
                                let displayValue;
                                if (key === 'description_ai' && typeof value === 'object' && value !== null) {
                                    // If description_ai is nested within aiGeneratedFields and is an object
                                    displayValue = renderDescriptionContent(value);
                                } else if (typeof value === 'object' && value !== null) {
                                    displayValue = <pre className="small mb-0" style={{ whiteSpace: 'pre-wrap', overflowX: 'auto' }}>{JSON.stringify(value, null, 2)}</pre>;
                                } else {
                                    displayValue = String(value);
                                }
                                return (
                                    <div key={key} className="mb-2"> {/* Increased margin-bottom */}
                                        <small><strong>{displayKey}:</strong></small>
                                        {/* If value was an object and handled by renderDescriptionContent, it returns JSX
                                        Otherwise, displayValue is a string or pre-formatted JSON string */}
                                        {key === 'description_ai' && typeof value === 'object' && value !== null ? displayValue : <div className="small" style={{ whiteSpace: 'pre-wrap' }}>{displayValue}</div>}
                                    </div>
                                );
                            })}

                            {source === 'ai_fallback' && description_ai && (
                                <>
                                    <hr />
                                    <h6 className="mt-2 text-info">AI Generated Description:</h6>
                                    {renderDescriptionContent(description_ai)}
                                </>
                            )}

                            {source === 'db' && dbData && (
                                <>
                                    <hr />
                                    <h5 className="text-success mt-3">Matched Exercise Details (from DB)</h5>
                                    <p><small><strong>Category:</strong> {dbData.category || 'N/A'}</small></p>
                                    <p><small><strong>Muscles:</strong> {(dbData.primaryMuscles || []).concat(dbData.secondaryMuscles || []).join(', ') || 'N/A'}</small></p>
                                    <h6>Instructions:</h6>
                                    <div style={{ maxHeight: '150px', overflowY: 'auto', fontSize: '0.85rem', backgroundColor: '#f8f9fa', padding: '8px', borderRadius: '4px', whiteSpace: 'pre-wrap' }}>
                                        {(dbData.instructions || []).length > 0 ? dbData.instructions.map((inst, i) => <p key={i} className="mb-1">{inst}</p>) : "No instructions."}
                                    </div>
                                </>
                            )}
                            {source === 'none' && !dbData && description_ai && (
                                <>
                                    <hr />
                                    <h6 className="mt-2 text-muted">Description:</h6>
                                    {renderDescriptionContent(description_ai)}
                                </>
                            )}
                            {source === 'pending_db' && <Spinner animation="grow" size="sm" />}
                            {source === 'pending_ai' && <Spinner animation="grow" size="sm" variant="info" />}
                        </Col>
                        {staticModalImageUrl && (
                            <Col md={6} className="text-center">
                                <div style={modalImageContainerStyle} onMouseEnter={() => { if (canShowGif) setIsModalImageHovering(true); }} onMouseLeave={() => { if (canShowGif) setIsModalImageHovering(false); }}>
                                    <img key={currentImageSrc} src={currentImageSrc || ''} alt={displayName} style={modalImageStyle} onError={(e) => { if (e.target.src === gifModalUrl && source === 'db' && dbData?.images?.[0]) e.target.src = `${DB_IMAGE_BASE_URL}${dbData.images[0]}`; else { e.target.style.display = 'none'; const placeholder = e.target.nextElementSibling; if (placeholder && placeholder.tagName === 'P') placeholder.style.display = 'block'; } }} />
                                    <p style={{ display: 'none', fontSize: '0.8rem', color: '#6c757d', marginTop: '0.5rem' }}>Image unavailable</p>
                                </div>
                            </Col>
                        )}
                    </Row>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={closeActivityDetailModal}>Close</Button>
                </Modal.Footer>
            </Modal>
        );
    };

    // Filter function specific to workout plans for HistoryView
    const workoutHistoryFilter = (items, term) => {
        return items.filter(item => {
            const plan = item.workoutPlan || {}; // Access the nested workoutPlan
            const name = item.name?.toLowerCase() || '';
            if (name.includes(term)) return true;

            // Search within session focus or activity names in the plan
            for (const dayKey in plan) {
                const dayData = plan[dayKey];
                if (dayData["Session Focus"]?.toLowerCase().includes(term)) return true;
                if (Array.isArray(dayData["Activities"])) {
                    if (dayData["Activities"].some(act => act["Activity Name"]?.toLowerCase().includes(term))) return true;
                }
            }
            return false;
        });
    };

    return (
        <ViewModeLayout
            viewMode={viewMode}
            onShowGeneratorView={handleShowGeneratorView}
            onShowHistoryView={handleShowHistoryView}
            historyTitle="Workout Plan History"
            generatorTitle="Workout Plan Generator"
            historyIcon={faHistory}
            generatorIcon={faDumbbell} // Specific icon for workout generator
            newItemButtonText="New Workout Plan"
            headerStyle={{ background: 'linear-gradient(to right, var(--bs-success), var(--bs-teal))' }} // Custom header gradient
        >
            {viewMode === 'history' ? (
                <HistoryView
                    items={workoutHistory}
                    isLoading={isLoadingHistory}
                    error={historyError}
                    onSelectItem={handleShowHistoryDetails}
                    onHideItem={handleHideWorkoutFromHistory}
                    renderItemSummary={renderWorkoutHistoryItemSummary}
                    searchPlaceholder="Search workout history..."
                    itemTypePlural="Workout Plans"
                    filterFunction={workoutHistoryFilter}
                />
            ) : (
                <>
                    {renderGeneratorForm()}
                    {generationError && <Alert variant="danger" className="mt-4">{generationError}</Alert>}
                    {loadingGeneration && !llmWorkoutPlan && (
                        <div className="text-center my-5">
                            <Spinner animation="border" variant="success" style={{ width: '3rem', height: '3rem' }} />
                            <p className="mt-3 text-muted">Generating your plan...</p>
                        </div>
                    )}
                    {/* Display newly generated plan */}
                    {llmWorkoutPlan && !loadingGeneration && renderWorkoutPlanDetailsContent(llmWorkoutPlan)}
                </>
            )}

            <ItemDetailsModal
                show={showHistoryModal}
                onHide={handleCloseHistoryModal}
                itemData={selectedHistoryWorkout} // This is { id, timestamp, workoutPlan, name }
                renderItemDetailsContent={(item) => renderWorkoutPlanDetailsContent(item.workoutPlan)} // Pass the actual plan object
                modalTitle={selectedHistoryWorkout?.name || "Workout Plan Details"}
            />

            {/* Modal for individual Exercise details (remains specific to this component) */}
            {renderActivityDetailModal()}

            {/* Component-specific styles, or move common ones to HistoryView.css */}
            <style jsx global>{`
                /* Styles from HistoryView.css will apply if class names match */
                /* Add any WeeklyWorkoutGenerator-specific styles here */
                .workout-activities-table th, .workout-activities-table td {
                    vertical-align: middle;
                }
            `}</style>
        </ViewModeLayout>
    );
};

export default WeeklyWorkoutGenerator;